import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import contactImage from '../Images/contact.avif';
import { submitContactForm } from '../lib/sanity/submitContact';

export default function ContactSection() {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => {
          setShowModal(false);
          setStatus('idle');
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to submit form. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <motion.section
      className="relative min-h-[600px] flex items-center justify-center overflow-hidden"
      style={{backgroundImage: `url(${contactImage})`, backgroundSize: 'cover', backgroundPosition: 'center'}}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 flex flex-col w-full max-w-4xl mx-auto items-center justify-center px-8 py-16">
        {/* Newsletter Section */}
        <div className="w-full flex flex-col items-center text-white mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center max-w-lg leading-tight">Join our Briefing</h2>
          <p className="mb-8 text-lg text-center max-w-md">Get weekly insights on market trends, investment strategies, and economic analysis.</p>
          <iframe 
            src="https://bigdealcapital.substack.com/embed" 
            width="100%" 
            height="320" 
            style={{ border: '1px solid #EEE', background: 'white', borderRadius: '0.75rem' }}
            frameBorder="0" 
            scrolling="no"
          />
        </div>

        {/* Contact Section */}
        <div className="w-full flex flex-col items-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center max-w-lg leading-tight">Contact bigDEAL Capital</h2>
          <p className="mb-8 text-lg text-center max-w-md">I am always learning, help me in sending your questions and feedback here!</p>
          <button
            className="border border-white text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-900 transition-colors shadow-lg"
            onClick={() => setShowModal(true)}
          >
            Contact Us
          </button>
        </div>
      </div>

      {/* Modal Popup */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative"
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-blue-900 text-2xl font-bold"
              onClick={() => {
                setShowModal(false);
                setStatus('idle');
                setErrorMessage('');
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-2xl font-bold mb-4 text-blue-900">Contact Us</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                className="px-4 py-3 rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={status === 'submitting'}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                className="px-4 py-3 rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={status === 'submitting'}
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                className="px-4 py-3 rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                required
                disabled={status === 'submitting'}
              />
              {errorMessage && (
                <p className="text-red-500 text-sm">{errorMessage}</p>
              )}
              <button
                type="submit"
                className={`mt-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  status === 'submitting'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : status === 'success'
                    ? 'bg-green-600'
                    : 'bg-blue-900 hover:bg-blue-800'
                } text-white`}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Sending...' : status === 'success' ? 'Sent!' : 'Send'}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.section>
  );
} 
